import { delay } from "../client.js";
import { formatCommandDateTime } from "../../pages/Accounts/commandUtils.js";

const STORAGE_KEY = "console-design.account-commands.v2";

const SEED_COMMANDS = {
  "acc-1": [
    {
      id: "cmd-1",
      assetId: "asset-1",
      dateTime: "21.03.2025 | 04:02:52 PM",
      model: "Daly_48V_2022_V1",
      name: "sample-name",
      description: "Sample CAN wake command for testing.",
      commandHex:
        "4C41356A0001001E000105001900010048EE000005010000000000000000000000000E8030001875E",
      createdBy: "devicemanagement",
      baud: 500,
      commandType: "can",
    },
    {
      id: "cmd-2",
      assetId: "asset-1",
      dateTime: "24.03.2025 | 09:41:10 AM",
      model: "Daly_48V_2022_V1",
      name: "BMS Wake",
      description: "Wakes the battery management system from sleep mode.",
      commandHex:
        "4C41356A0001001E000105001900010048EE000005010000000000000000000000000E8030001875E",
      createdBy: "karenaneja",
      baud: 500,
      commandType: "can",
    },
    {
      id: "cmd-3",
      assetId: "asset-2",
      dateTime: "25.03.2025 | 02:15:33 PM",
      model: "Daly_72V_2023_V2",
      name: "Cell Balance",
      description: "Triggers cell balancing on the 72V pack.",
      commandHex: "4C41356A0001002A00010500F4010002000100000000000000000000000002645",
      createdBy: "devicemanagement",
      baud: 250,
      commandType: "can",
    },
  ],
};

function cloneSeed() {
  return Object.fromEntries(
    Object.entries(SEED_COMMANDS).map(([id, rows]) => [id, rows.map((row) => ({ ...row }))]),
  );
}

function loadStore() {
  const merged = cloneSeed();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return merged;
    const parsed = JSON.parse(raw);
    for (const [accountId, rows] of Object.entries(parsed)) {
      if (Array.isArray(rows)) merged[accountId] = rows;
    }
  } catch {
    /* ignore */
  }
  return merged;
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
}

function nextId() {
  return `cmd-${Date.now().toString(36)}`;
}

function getAccountCommands(store, accountId) {
  if (!store[accountId]) {
    store[accountId] = SEED_COMMANDS[accountId]
      ? SEED_COMMANDS[accountId].map((row) => ({ ...row }))
      : [];
  }
  return store[accountId];
}

function normalizePayload(payload) {
  const name = payload.name?.trim();
  if (!name) throw new Error("Command name is required");
  const assetId = payload.assetId?.trim();
  if (!assetId) throw new Error("Asset is required");
  return {
    assetId,
    name,
    description: String(payload.description ?? "").trim(),
    commandType: payload.commandType?.trim() || "can",
    model: String(payload.model ?? "").trim(),
    commandHex: String(payload.commandHex ?? "").replace(/\s/g, "").toUpperCase(),
    baud: Number(payload.baud) || 500,
    createdBy: payload.createdBy?.trim() || "devicemanagement",
  };
}

export async function listAccountCommands(accountId) {
  await delay(120);
  const store = loadStore();
  return [...getAccountCommands(store, accountId)];
}

export async function createAccountCommand(accountId, payload) {
  await delay(140);
  const data = normalizePayload(payload);
  const store = loadStore();
  const rows = getAccountCommands(store, accountId);
  const row = { id: nextId(), dateTime: formatCommandDateTime(), ...data };
  rows.unshift(row);
  saveStore(store);
  return { ...row };
}

export async function updateAccountCommand(accountId, commandId, payload) {
  await delay(120);
  const data = normalizePayload(payload);
  const store = loadStore();
  const rows = getAccountCommands(store, accountId);
  const row = rows.find((r) => r.id === commandId);
  if (!row) throw new Error("Command not found");
  Object.assign(row, data, { dateTime: formatCommandDateTime() });
  saveStore(store);
  return { ...row };
}

export async function deleteAccountCommand(accountId, commandId) {
  await delay(100);
  const store = loadStore();
  const rows = getAccountCommands(store, accountId);
  const index = rows.findIndex((r) => r.id === commandId);
  if (index < 0) throw new Error("Command not found");
  rows.splice(index, 1);
  saveStore(store);
}
